import {Sex} from 'ApplicationEntry/models/Sex';
import {ApplicantType} from 'ApplicationEntry/models/ApplicantType';
import {
  EmptySocialSecurityNumber,
  isValidSocialSecurityNumber,
  SocialSecurityNumber,
  UnvalidSocialSecurityNumber,
  ValidSocialSecurityNumber,
} from 'ApplicationEntry/models/SocialSecurityNumber';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

import {isValidDate} from 'ApplicationEntry/models/Date';
import {Email, EmptyEmail, isValidEmail, ValidEmail} from 'ApplicationEntry/models/Email';
import {EmptyPhoneNumber, isValidPhoneNumber, PhoneNumber, ValidPhoneNumber} from 'ApplicationEntry/models/PhoneNumber';
import {EmptyZipCode, isValidZipCode, UnvalidatedZipCode, ValidZipCode, ZipCode} from 'ApplicationEntry/models/ZipCode';
import {EmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {Occupation, OccupationError, UserAssignedOccupation} from './Occupation';

import {Async} from 'Core/models/Async';

export class InvalidGroupApplicantDemographics {
  public readonly key = 'InvalidGroupApplicantDemographics';

  constructor(
    public readonly name: StringUnion,
    public readonly type: ApplicantType | '',
    public readonly sex: Sex | '',
    public readonly dateOfBirth: Date | '',
    public readonly address: StringUnion,
    public readonly addressLine2: StringUnion,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly socialSecurityNumber: SocialSecurityNumber,
    public readonly hireDate: Date | '',
    public readonly email: Email,
    public readonly phone: PhoneNumber,
    public readonly occupation: Occupation,
    public readonly occSmokerCode: StringUnion
  ) {}
}

type AppProps = {
  name: StringUnion;
  type: ApplicantType | '';
  sex: Sex | '';
  dateOfBirth: Date | '';
  address: StringUnion;
  addressLine2: StringUnion;
  city: StringUnion;
  state: StringUnion;
  zip: ZipCode;
  socialSecurityNumber: SocialSecurityNumber;
  hireDate: Date | '';
  email: Email;
  phone: PhoneNumber;
  occupation: Occupation;
  occSmokerCode: StringUnion;
};

export class ValidGroupApplicantDemographics {
  public static createFromApp({
    name,
    type,
    sex,
    dateOfBirth,
    address,
    addressLine2,
    city,
    state,
    zip,
    socialSecurityNumber,
    hireDate,
    email,
    phone,
    occupation,
    occSmokerCode,
  }: AppProps) {
    return this.create(
      name,
      type,
      sex,
      dateOfBirth,
      address,
      addressLine2,
      city,
      state,
      zip,
      socialSecurityNumber,
      hireDate,
      email,
      phone,
      occupation,
      occSmokerCode
    );
  }

  public static create(
    name: StringUnion,
    type: ApplicantType | '',
    sex: Sex | '',
    dateOfBirth: Date | '',
    address: StringUnion,
    addressLine2: StringUnion,
    city: StringUnion,
    state: StringUnion,
    zip: ZipCode,
    socialSecurityNumber: SocialSecurityNumber,
    hireDate: Date | '',
    email: Email,
    phone: PhoneNumber,
    occupation: Occupation,
    occSmokerCode: StringUnion
  ) {
    return isValidZipCode(zip) &&
      isValidPhoneNumber(phone) &&
      isValidSocialSecurityNumber(socialSecurityNumber) &&
      isValidEmail(email) &&
      isValidDate(dateOfBirth) &&
      isValidDate(hireDate)
      ? new ValidGroupApplicantDemographics(
          name,
          type,
          sex,
          dateOfBirth,
          address,
          addressLine2,
          city,
          state,
          zip,
          socialSecurityNumber,
          hireDate,
          email,
          phone,
          occupation,
          occSmokerCode
        )
      : new InvalidGroupApplicantDemographics(
          name,
          type,
          sex,
          dateOfBirth,
          address,
          addressLine2,
          city,
          state,
          zip,
          socialSecurityNumber,
          hireDate,
          email,
          phone,
          occupation,
          occSmokerCode
        );
  }

  public readonly key = 'ValidGroupApplicantDemographics';

  constructor(
    public readonly name: StringUnion,
    public readonly type: ApplicantType | '',
    public readonly sex: Sex | '',
    public readonly dateOfBirth: Date | '',
    public readonly address: StringUnion,
    public readonly addressLine2: StringUnion,
    public readonly city: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ValidZipCode | UnvalidatedZipCode,
    public readonly socialSecurityNumber: ValidSocialSecurityNumber | UnvalidSocialSecurityNumber,
    public readonly hireDate: Date | '',
    public readonly email: ValidEmail,
    public readonly phone: ValidPhoneNumber,
    public readonly occupation: Occupation,
    public readonly occSmokerCode: StringUnion
  ) {}
}

export type GroupApplicantDemographics = ValidGroupApplicantDemographics | InvalidGroupApplicantDemographics;

export type GroupApplicantDemographicsOptions<TReturn> = ToMatchOptions<GroupApplicantDemographics, 'key', TReturn>;

export const matchGroupApplicantDemographics = <TReturn>(
  applicantDemographics: GroupApplicantDemographics,
  options: GroupApplicantDemographicsOptions<TReturn>
): TReturn => {
  switch (applicantDemographics.key) {
    case 'InvalidGroupApplicantDemographics':
      return options.InvalidGroupApplicantDemographics(applicantDemographics);
    case 'ValidGroupApplicantDemographics':
      return options.ValidGroupApplicantDemographics(applicantDemographics);
  }
};

export const EmptyGroupApplicantDemographics = new ValidGroupApplicantDemographics(
  new EmptyString(),
  '',
  '',
  '',
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  EmptyZipCode,
  EmptySocialSecurityNumber,
  '',
  EmptyEmail,
  EmptyPhoneNumber,
  new UserAssignedOccupation('', '', '', ''),
  new EmptyString()
);

export const changeName = (name: string) => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({
    ...app,
    name: NonEmptyString.create(name),
  });

export const changeSex = (sex: Sex) => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({...app, sex});

export const changeDateOfBirth = (dateOfBirth: Date | '') => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({...app, dateOfBirth});

export const changeAddress = (address: string) => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({...app, address: NonEmptyString.create(address)});

export const changeAddressLine2 = (addressLine2: string) => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({...app, addressLine2: NonEmptyString.create(addressLine2)});

export const changeCity = (city: string) => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({...app, city: NonEmptyString.create(city)});

export const changeState = (state: string) => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({...app, state: NonEmptyString.create(state)});

export const changeZip = (zip: ZipCode) => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({...app, zip});

export const changeSocialSecurityNumber = (socialSecurityNumber: string) => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({
    ...app,
    socialSecurityNumber: ValidSocialSecurityNumber.create(socialSecurityNumber),
  });

export const changeHireDate = (hireDate: Date | '') => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({...app, hireDate});

export const changeEmail = (email: string) => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({...app, email: ValidEmail.create(email)});

export const changePhone = (phone: string) => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({...app, phone: ValidPhoneNumber.create(phone)});

export const changeOccupation = (occupation: Async<string>) => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({...app, occupation});

export const changeUserOccupation = (
  occupation: string,
  code: string,
  description: string,
  underwritingCode: string
) => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({
    ...app,
    occupation: UserAssignedOccupation.create(occupation, code, description, underwritingCode),
  });

export const changeOccupationError = (occupation: string) => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({...app, occupation: OccupationError.create(occupation)});

export const changeOccSmokerCode = (occSmokerCode: string) => (app: GroupApplicantDemographics) =>
  ValidGroupApplicantDemographics.createFromApp({...app, occSmokerCode: NonEmptyString.create(occSmokerCode)});
